/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import OpenIcon from '@atlaskit/icon/core/migration/arrow-up-right--open';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAnalytics } from '../../analytics';
import { CLOUD_ADMIN_URL, adminHubManageUsersHref } from '../../utils';
import { messages } from '../i18n';
import { DropdownMenuAnalytics } from './DropdownMenuAnalytics';
import { useInvitePeopleContext } from '../InvitePeople';
import type { AdminMenuGroupComponentProps } from '../../types';

const styles = cssMap({
	dropdownItemIcon: {
		marginRight: token('space.100', '8px'),
	},
});

const AdminMenuGroup = ({ onAdminMenuItemClick }: AdminMenuGroupComponentProps) => {
	const invitePeopleData = useInvitePeopleContext();
	const cloudId = invitePeopleData.cloudId ?? '';
	const analytics = useAnalytics(DropdownMenuAnalytics);
	const { formatMessage } = useIntl();
	const adminMenuTitle = formatMessage(messages.adminDropdownItemGroupTitle);
	const onSiteAccessSettingsMenuItemClick = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			onAdminMenuItemClick?.(event);

			analytics.dropdownMenuItemClicked({
				actionSubjectId: 'siteAccessSettingsLink',
				attributes: {
					integration: invitePeopleData?.source,
					product: invitePeopleData?.productId,
				},
			});
		},
		[analytics, invitePeopleData?.productId, invitePeopleData?.source, onAdminMenuItemClick],
	);
	const onUserManagementMenuItemClick = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			onAdminMenuItemClick?.(event);

			analytics.dropdownMenuItemClicked({
				actionSubjectId: 'userManagementLink',
				attributes: {
					integration: invitePeopleData?.source,
					product: invitePeopleData?.productId,
				},
			});
		},
		[analytics, invitePeopleData?.productId, invitePeopleData?.source, onAdminMenuItemClick],
	);

	const userManagementMenuItem = useMemo(() => {
		return (
			<DropdownItem
				testId="testId-invite-people-user-management-menu-item"
				href={adminHubManageUsersHref(cloudId)}
				onClick={onUserManagementMenuItemClick}
				target="new"
			>
				<Inline alignBlock="center">
					<Box as="span" xcss={styles.dropdownItemIcon}>
						<OpenIcon color="currentColor" label="open" LEGACY_size="small" />
					</Box>
					<Text>
						<FormattedMessage {...messages.formUserManagementLabel} />
					</Text>
				</Inline>
			</DropdownItem>
		);
	}, [cloudId, onUserManagementMenuItemClick]);

	const siteAccessSettingsMenuItem = useMemo(() => {
		return (
			<DropdownItem
				testId="testId-invite-people-manage-access-settings-menu-item"
				href={`${CLOUD_ADMIN_URL}/s/${cloudId}/signup`}
				onClick={onSiteAccessSettingsMenuItemClick}
				target="new"
			>
				<Inline alignBlock="center">
					<Box as="span" xcss={styles.dropdownItemIcon}>
						<OpenIcon color="currentColor" label="open" LEGACY_size="small" />
					</Box>
					<Text>
						<FormattedMessage {...messages.formManageAccessSettingsLabel} />
					</Text>
				</Inline>
			</DropdownItem>
		);
	}, [cloudId, onSiteAccessSettingsMenuItemClick]);

	return (
		<DropdownItemGroup title={adminMenuTitle}>
			{userManagementMenuItem}
			{siteAccessSettingsMenuItem}
		</DropdownItemGroup>
	);
};

export default AdminMenuGroup;
